import { useCallback } from 'react';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import type { NgoaiTe, NgoaiTeInput } from '@/types/schemas';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { currencyColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function DanhMucNgoaiTe() {
  const {
    data,
    isLoading,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    totalItems,
    currentPage,
    handlePageChange,
    fetchDetail
  } = useCRUD<NgoaiTe, NgoaiTeInput>({
    endpoint: QUERY_KEYS.NGOAI_TE,
    pageSize: PAGE_SIZE
  });
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<NgoaiTe>(fetchDetail);

  const {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleRefreshClick: handleDynamicRefresh,
    isFiltering
  } = useSearchTable({
    handlePageChange
  });

  const handleSubmit = async (data: NgoaiTeInput) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, data);
      }

      handleCloseForm();
      clearSelection();
      await refreshData();
    } catch (error) {
      return;
    }
  };

  const handleRefresh = useCallback(async () => {
    if (hasFilterParams) {
      await handleDynamicRefresh();
    } else {
      await refreshData();
    }
  }, [hasFilterParams, handleDynamicRefresh, refreshData]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const isTableLoading = isLoading || isFiltering;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: currencyColumns
    }
  ];

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          formMode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          onDelete={deleteItem}
          clearSelection={clearSelection}
        />
      )}

      <div className='w-full'>
        <ActionBar
          onAddIconClick={handleAddClick}
          onEditIconClick={() => selectedObj && handleEditClick()}
          onDeleteIconClick={() => selectedObj && handleDeleteClick()}
          onCopyIconClick={() => selectedObj && handleCopyClick()}
          onWatchIconClick={() => selectedObj && handleViewClick()}
          onRefreshClick={handleRefresh}
          isViewDisabled={!selectedObj}
        />

        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex || undefined}
          onRowClick={handleRowClick}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          serverSidePagination
          onSearchChange={handleSearchChange}
          loading={isTableLoading}
        />
      </div>
    </div>
  );
}
